import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  BackendAuthResponse,
  OAuth2TokenResponse,
  User,
  UserAuthResponse,
} from '../../shared/models/api.models';
import { SettingsService } from '../api/settings.service';
import {
  Feature,
  PermissionAction,
  ROLES,
  canPerformAction,
  hasPermission as checkPermission,
  getFeaturePermissions,
  hasAllPermissions,
  hasAnyPermission,
} from '../security';
import { AuthProviderService } from './auth-provider.service';
import { DEFAULT_LANDING_ROUTE } from './landing-route';
import { OAuth2Service } from './oauth.service';
import { PingFederateOAuthService } from './ping-federate-oauth.service';

// Legacy interfaces for backward compatibility
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

// Helper interface for internal user representation with permissions
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private oauthService = inject(OAuth2Service);
  private pingFederateService = inject(PingFederateOAuthService);
  private settingsService = inject(SettingsService);
  private authProviderService = inject(AuthProviderService);
  private apiUrl = `${environment.apiUrl}/auth`;

  // Signal-based state management
  private _user = signal<AuthUser | null>(null);
  private _isLoading = signal(false);
  private _error = signal<string | null>(null);

  // Public readonly signals
  readonly user = this._user.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly error = this._error.asReadonly();

  // Computed signals
  readonly isAuthenticated = computed(() => !!this._user());
  readonly userRoles = computed(() => this._user()?.roles || []);
  readonly userPermissions = computed(() => this._user()?.permissions || []);

  // Token refresh coordination (shared across interceptor invocations to prevent
  // multiple concurrent refresh attempts when several requests get 401 simultaneously)
  private _isRefreshing = false;
  private readonly _refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // Cooldown after a failed refresh to prevent infinite retry storms
  // (e.g., expired SPA refresh token → every caller retries immediately).
  private _lastRefreshFailureTime = 0;
  private static readonly REFRESH_COOLDOWN_MS = 10_000; // 10 seconds

  constructor() {
    this.initializeAuth();

    // FOR DEVELOPMENT: Auto-authenticate with mock user
    // this.enableMockAuthentication(); // Disabled to use real authentication
  }

  private initializeAuth(): void {
    // Priority 1: Check for backend auth data (after OAuth validation)
    const backendToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    const hasStoredRefreshToken = !!(
      localStorage.getItem('oauth_refresh_token') || localStorage.getItem('auth_refresh_token')
    );

    if (backendToken && storedUser) {
      const backendTokenExpiry = localStorage.getItem('auth_token_expiry');
      const isExpired = !backendTokenExpiry || Date.now() >= parseInt(backendTokenExpiry);

      // Restore user if token is valid OR if we have a refresh token.
      // Do NOT refresh here — the auth guard handles proactive refresh before
      // components load, and the interceptor handles 401s during the session.
      // Refreshing here would race with the interceptor and consume the
      // one-time-use refresh token, causing the interceptor's refresh to fail.
      if (!isExpired || hasStoredRefreshToken) {
        try {
          const user = JSON.parse(storedUser);
          const authUser = this.mapApiUserToAuthUser(user);
          this._user.set(authUser);

          // Initialize AG Grid license when restoring session
          this.settingsService.initializeAgGridLicense().subscribe({
            error: error => {
              console.error(
                '[AuthService] Failed to initialize AG Grid license after session restore:',
                error
              );
            },
          });
        } catch (e) {
          console.error('Failed to parse stored backend user:', e);
          this.clearBackendAuthData();
        }
        return;
      }
    }

    // Priority 2: Check for OAuth tokens (during OAuth flow)
    const oauthToken = localStorage.getItem('oauth_access_token');
    if (oauthToken) {
      // Restore OAuth user from localStorage
      const oauthStoredUser = localStorage.getItem('oauth_user');
      if (oauthStoredUser) {
        try {
          const user = JSON.parse(oauthStoredUser);
          this._user.set(user);
        } catch (e) {
          console.error('Failed to parse stored user:', e);
          this.clearStoredToken();
        }
      }
      return;
    }

    // Priority 3: Fall back to regular token check (traditional login)
    const storedToken = this.getStoredToken();
    if (!storedToken) return;

    this._isLoading.set(true);
    this.getCurrentUser().subscribe({
      next: user => {
        if (user) {
          this._user.set(user);
        }
        this._isLoading.set(false);
      },
      error: () => {
        this.clearStoredToken();
        this._isLoading.set(false);
      },
    });
  }

  /**
   * Login with credentials
   * Uses POST /api/auth endpoint from Postman collection
   */
  login(credentials: LoginCredentials): Observable<boolean> {
    this._isLoading.set(true);
    this._error.set(null);

    return this.http.post<UserAuthResponse>(this.apiUrl, credentials).pipe(
      tap(response => {
        if (response.isSuccess && response.value) {
          // Convert API User to AuthUser format
          const authUser = this.mapApiUserToAuthUser(response.value);
          this._user.set(authUser);

          // For now, store a mock token since OAuth will handle real tokens
          this.storeMockToken();

          // Initialize AG Grid license after successful login
          this.settingsService.initializeAgGridLicense().subscribe({
            error: error => {
              console.error(
                '[AuthService] Failed to initialize AG Grid license after login:',
                error
              );
            },
          });
        } else {
          this._error.set(response.error || 'Login failed');
        }
      }),
      map(response => response.isSuccess),
      catchError(error => {
        const errorMessage = error.error?.error || error.error?.message || 'Login failed';
        this._error.set(errorMessage);
        return of(false);
      }),
      tap(() => this._isLoading.set(false))
    );
  }

  /**
   * OAuth2 signin method for Microsoft Azure AD
   * Uses PKCE authorization code flow
   */
  signInWithOAuth(): void {
    try {
      this.authProviderService.setProvider('entra');
      // Start OAuth2 authorization flow
      this.oauthService.authorize();
    } catch (error) {
      console.error('OAuth2 authorization failed:', error);
      this._error.set('OAuth2 signin failed to start');
    }
  }

  /**
   * PingFederate OIDC signin
   * Uses PKCE authorization code flow with login_hint
   */
  signInWithPingFederate(): void {
    try {
      this.authProviderService.setProvider('pingfederate');
      this.pingFederateService.authorize();
    } catch (error) {
      console.error('PingFederate authorization failed:', error);
      this._error.set('PingFederate signin failed to start');
    }
  }

  /**
   * Handle OAuth2 callback after user returns from authorization server
   * Call this method in your OAuth callback component
   */
  handleOAuthCallback(): void {
    const provider = this.authProviderService.getSessionProvider();

    if (provider === 'pingfederate') {
      this.handlePingFederateCallback();
    } else {
      this.handleAzureAdCallback();
    }
  }

  private handleAzureAdCallback(): void {
    if (this.oauthService.isOAuthCallback()) {
      const params = this.oauthService.getCallbackParams();

      if (params) {
        this._isLoading.set(true);

        this.oauthService
          .handleCallback(params.code, params.state)
          .pipe(
            tap(tokenResponse => {
              this.storeOAuthTokens(tokenResponse);
            }),
            switchMap(tokenResponse => this.validateOAuthWithBackend(tokenResponse.access_token)),
            tap(backendAuthResponse => {
              if (backendAuthResponse) {
                this.storeBackendAuthData(backendAuthResponse);
                const authUser = this.mapApiUserToAuthUser(backendAuthResponse.user);
                this._user.set(authUser);
                this.initializeAgGridLicenseAfterLogin();
              }
            })
          )
          .subscribe({
            next: backendAuthResponse => {
              if (backendAuthResponse) {
                this.oauthService.clearAuthParams();
                const redirectRoute = this.getPostLoginRedirectRoute();
                window.history.replaceState({}, document.title, redirectRoute);
                this.router.navigate([redirectRoute]);
              } else {
                console.error('Backend OAuth validation failed');
                this._error.set(this._error() || 'Authentication failed. Please try again.');
                this.oauthService.clearAuthParams();
                this.router.navigate(['/auth/login']);
              }
            },
            error: error => {
              console.error('OAuth2 authentication error:', error);
              this._error.set('Authentication failed. Please try again.');
              this.oauthService.clearAuthParams();
              this.router.navigate(['/auth/login']);
            },
            complete: () => {
              this._isLoading.set(false);
            },
          });
      } else {
        console.error('Auth Service: Missing OAuth callback parameters');
        this._error.set('Invalid authentication response. Please try again.');
        this.router.navigate(['/auth/login']);
      }
    } else {
      this.router.navigate(['/auth/login']);
    }
  }

  private handlePingFederateCallback(): void {
    if (this.pingFederateService.isOAuthCallback()) {
      const params = this.pingFederateService.getCallbackParams();

      if (params) {
        this._isLoading.set(true);

        this.pingFederateService
          .handleCallback(params.code, params.state)
          .pipe(
            tap(tokenResponse => {
              this.storeOAuthTokens(tokenResponse);
            }),
            switchMap(tokenResponse => this.validateOAuthWithBackend(tokenResponse.access_token)),
            tap(backendAuthResponse => {
              if (backendAuthResponse) {
                this.storeBackendAuthData(backendAuthResponse);
                const authUser = this.mapApiUserToAuthUser(backendAuthResponse.user);
                this._user.set(authUser);
                this.initializeAgGridLicenseAfterLogin();
              }
            })
          )
          .subscribe({
            next: backendAuthResponse => {
              if (backendAuthResponse) {
                this.pingFederateService.clearAuthParams();
                const redirectRoute = this.getPostLoginRedirectRoute();
                window.history.replaceState({}, document.title, redirectRoute);
                this.router.navigate([redirectRoute]);
              } else {
                console.warn(
                  'Backend validation failed (expected — backend PF support not yet implemented)'
                );
                this._error.set(
                  this._error() ||
                    'PingFederate authentication succeeded but backend validation is not yet configured.'
                );
                this.pingFederateService.clearAuthParams();
                this.router.navigate(['/auth/login']);
              }
            },
            error: error => {
              console.error('PingFederate authentication error:', error);
              this._error.set('PingFederate authentication failed. Please try again.');
              this.pingFederateService.clearAuthParams();
              this.router.navigate(['/auth/login']);
            },
            complete: () => {
              this._isLoading.set(false);
            },
          });
      } else {
        this._error.set('Invalid PingFederate authentication response.');
        this.router.navigate(['/auth/login']);
      }
    } else {
      this.router.navigate(['/auth/login']);
    }
  }

  logout(): void {
    const logoutUrl = this.authProviderService.getLogoutUrl();

    // Clear all auth data including OAuth
    this.clearOAuthData();
    // Clear OAuth session storage data
    this.clearOAuthSessionData();
    // Clear AG Grid license cache on logout
    this.settingsService.clearCachedLicense();
    this._user.set(null);
    this._error.set(null);

    if (logoutUrl) {
      window.location.href = logoutUrl;
      return;
    }

    // Redirect to login page
    this.router.navigate(['/auth/login']);
  }

  /**
   * Clear OAuth session storage data (PKCE parameters)
   */
  private clearOAuthSessionData(): void {
    sessionStorage.removeItem('oauth_code_verifier');
    sessionStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_origin');
    this.authProviderService.clearProvider();
  }

  /**
   * Attempt to refresh the authentication token.
   * Priority: OAuth refresh token > backend refresh token > legacy refresh token.
   * Returns Observable<boolean> — true on success, false on failure.
   * Callers are responsible for handling failure (e.g., logout, redirect).
   */
  refreshToken(): Observable<boolean> {
    // Prevent infinite retry storms: if a refresh just failed, skip immediately.
    const elapsed = Date.now() - this._lastRefreshFailureTime;
    if (this._lastRefreshFailureTime && elapsed < AuthService.REFRESH_COOLDOWN_MS) {
      console.warn(
        `[AuthService] refreshToken() skipped — cooldown active (${Math.round(
          elapsed / 1000
        )}s since last failure)`
      );
      return of(false);
    }

    // Priority 1: Try OAuth refresh token (primary auth method)
    const oauthRefreshToken = localStorage.getItem('oauth_refresh_token');
    if (oauthRefreshToken) {
      return this.refreshOAuthToken(oauthRefreshToken);
    }

    // Priority 2: Try backend refresh token (may also be an OAuth refresh token)
    const backendRefreshToken = localStorage.getItem('auth_refresh_token');
    if (backendRefreshToken) {
      return this.refreshOAuthToken(backendRefreshToken);
    }

    // Priority 3: Legacy refresh token (traditional login)
    const legacyRefreshToken = this.getStoredRefreshToken();
    if (!legacyRefreshToken) {
      console.warn('[AuthService] No refresh token found in any storage key — cannot refresh');
      return of(false);
    }

    return this.http
      .post<AuthTokens>('/api/auth/refresh', { refreshToken: legacyRefreshToken })
      .pipe(
        tap(tokens => this.storeTokens(tokens)),
        map(() => true),
        catchError(error => {
          this._lastRefreshFailureTime = Date.now();
          console.error('[AuthService] Legacy token refresh failed:', error);
          return of(false);
        })
      );
  }

  /**
   * Refresh the OAuth access token using Microsoft's token endpoint.
   * The refresh token is one-time use; a new refresh token is returned on each refresh.
   */
  private refreshOAuthToken(refreshToken: string): Observable<boolean> {
    return this.authProviderService.refreshAccessToken(refreshToken).pipe(
      tap(tokenResponse => {
        // Store new OAuth tokens (access_token + new one-time-use refresh_token)
        this.storeOAuthTokens(tokenResponse);
        // Update backend auth data since auth_token IS the OAuth access token
        this.updateBackendAuthTokenFromOAuth(tokenResponse);
      }),
      map(() => true),
      catchError(error => {
        this._lastRefreshFailureTime = Date.now();
        return of(false);
      })
    );
  }

  /**
   * Update backend auth token and expiry from a fresh OAuth token response.
   * Since auth_token stored in localStorage is the OAuth access token itself,
   * we need to update it when the OAuth token is refreshed.
   */
  private updateBackendAuthTokenFromOAuth(tokenResponse: OAuth2TokenResponse): void {
    localStorage.setItem('auth_token', tokenResponse.access_token);
    localStorage.setItem(
      'auth_token_expiry',
      (Date.now() + (tokenResponse.expires_in || 3600) * 1000).toString()
    );
  }

  hasRole(role: string): boolean {
    return this.userRoles().includes(role);
  }

  hasPermission(permission: string): boolean {
    return checkPermission(this.userPermissions(), permission);
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  hasAllRoles(roles: string[]): boolean {
    return roles.every(role => this.hasRole(role));
  }

  isDefaultRoleUser(): boolean {
    const roles = this.getNormalizedUserRoles();
    const defaultRole = this.normalizeRoleName(ROLES.DEFAULT_ROLE);

    return roles.length === 1 && roles[0] === defaultRole;
  }

  /**
   * Check if user has any of the specified permissions
   * @param permissions Array of permission strings to check
   * @returns True if user has at least one of the permissions
   */
  hasAnyPermissions(permissions: string[]): boolean {
    return hasAnyPermission(this.userPermissions(), permissions);
  }

  /**
   * Check if user has all of the specified permissions
   * @param permissions Array of permission strings to check
   * @returns True if user has all the permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    return hasAllPermissions(this.userPermissions(), permissions);
  }

  /**
   * Check if user can perform a specific action on a feature
   * Uses the security matrix for type-safe permission checking
   * @param feature The feature to check (e.g., FEATURES.PROVIDERS)
   * @param action The action to check (e.g., PERMISSION_ACTIONS.ADD)
   * @returns True if user can perform the action
   */
  canPerformAction(feature: Feature, action: PermissionAction): boolean {
    return canPerformAction(this.userPermissions(), feature, action);
  }

  /**
   * Get all permissions the user has for a specific feature
   * @param feature The feature to check
   * @returns Array of permission strings for the feature
   */
  getFeaturePermissions(feature: Feature): string[] {
    return getFeaturePermissions(this.userPermissions(), feature);
  }

  /**
   * Validate OAuth token with backend API and get user data
   * Sends OAuth token as Authorization Bearer header to backend for validation
   * Backend returns user data, and we continue using the OAuth token for all API calls
   * Follows Postman collection pattern: OAuth token is used throughout the session
   */
  validateOAuthWithBackend(oauthToken: string): Observable<BackendAuthResponse | null> {
    this._isLoading.set(true);
    this._error.set(null);

    const headers = {
      Authorization: `Bearer ${oauthToken}`,
    };

    return this.http.post<UserAuthResponse>(`${this.apiUrl}`, {}, { headers }).pipe(
      map(response => {
        if (response.isSuccess && response.value) {
          // Backend validates OAuth token and returns User data directly
          // Continue using the OAuth token for all subsequent API calls
          const backendResponse: BackendAuthResponse = {
            authToken: oauthToken, // Continue using OAuth token - backend expects this for all requests
            expiresIn: 60 * 60, // 1 hour (typical OAuth access token expiry)
            user: response.value,
          };
          return backendResponse;
        } else {
          this._error.set(response.error || 'OAuth validation failed');
          return null;
        }
      }),
      catchError(error => {
        const errorMessage =
          error.error?.error || error.error?.message || 'OAuth validation failed';
        this._error.set(errorMessage);
        console.error('Backend OAuth validation failed:', error);
        return of(null);
      }),
      tap(() => this._isLoading.set(false))
    );
  }

  /**
   * Initialize AG Grid license after successful login (non-blocking side effect)
   * This is fire-and-forget to avoid blocking the authentication flow
   */
  private initializeAgGridLicenseAfterLogin(): void {
    this.settingsService.initializeAgGridLicense().subscribe({
      error: error => {
        console.error(
          '[AuthService] Failed to initialize AG Grid license after OAuth login:',
          error
        );
      },
    });
  }

  getPostLoginRedirectRoute(): string {
    return this.isDefaultRoleUser() ? '/unauthorized' : DEFAULT_LANDING_ROUTE;
  }

  private getNormalizedUserRoles(): string[] {
    return this.userRoles()
      .map(role => this.normalizeRoleName(role))
      .filter(role => role.length > 0);
  }

  private normalizeRoleName(role: string): string {
    return role.replace(/\s+/g, '').toLowerCase();
  }

  private getCurrentUser(): Observable<AuthUser | null> {
    // TODO: Implement /api/auth/me endpoint or use user service
    return of(null);
  }

  /**
   * Map API User to internal AuthUser format
   * Handles both legacy format (roles as UserRole[]) and new format (roles as string[], permissions as string[])
   */
  private mapApiUserToAuthUser(apiUser: User): AuthUser {
    // Extract role names - handle both formats
    const roleNames = this.extractRoleNames(apiUser.roles);

    // Get permissions from API response (new format) or derive from roles/claims (legacy)
    const permissions = this.extractPermissions(apiUser, roleNames);

    return {
      id: apiUser.id.toString(), // Convert number to string for compatibility
      email: apiUser.emailAddress,
      name: `${apiUser.firstName} ${apiUser.lastName}`,
      roles: roleNames,
      permissions: permissions,
    };
  }

  /**
   * Extract role names from the API response
   * Handles both formats:
   * - New format: roles is string[] (e.g., ["ProgramTeam", "Admin"])
   * - Legacy format: roles is UserRole[] (e.g., [{ roleName: "Admin", claims: [...] }])
   */
  private extractRoleNames(roles: User['roles']): string[] {
    if (!roles || roles.length === 0) {
      return [];
    }

    // Check if it's the new format (array of strings)
    if (typeof roles[0] === 'string') {
      return roles as string[];
    }

    // Legacy format (array of UserRole objects)
    return (roles as { roleName: string; claims: string[] }[]).map(role => role.roleName);
  }

  /**
   * Extract permissions from the API response
   * Priority:
   * 1. Use permissions array from API response (new format)
   * 2. Fall back to claims + derived permissions (legacy format)
   */
  private extractPermissions(apiUser: User, roleNames: string[]): string[] {
    // New format: permissions are provided directly from the API
    if (apiUser.permissions && apiUser.permissions.length > 0) {
      return [...apiUser.permissions];
    }

    // Legacy format: derive from claims and roles
    const userClaims = apiUser.claims || [];
    const derivedPermissions = this.derivePermissionsFromRoles(roleNames);

    return [...new Set([...userClaims, ...derivedPermissions])];
  }

  /**
   * Derive permissions from user roles (legacy fallback)
   * This is used when the API doesn't provide explicit permissions
   */
  private derivePermissionsFromRoles(roles: string[]): string[] {
    const permissions: string[] = ['read']; // Base permission for all users

    if (roles.includes('Admin') || roles.includes('admin')) {
      permissions.push('write', 'delete', 'manage_users', 'manage_assets', 'manage_locations');
    }

    if (roles.includes('Manager') || roles.includes('manager')) {
      permissions.push('write', 'manage_assets', 'manage_locations');
    }

    if (roles.includes('User') || roles.includes('user')) {
      // Users already have base 'read' permission
    }

    return permissions;
  }

  /**
   * Store mock token for development
   */
  private storeMockToken(): void {
    const mockToken = 'mock-api-token-' + Date.now();
    const expiresIn = 24 * 60 * 60; // 24 hours

    localStorage.setItem('accessToken', mockToken);
    localStorage.setItem('tokenExpiry', (Date.now() + expiresIn * 1000).toString());
  }

  private storeTokens(tokens: AuthTokens): void {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    localStorage.setItem('tokenExpiry', (Date.now() + tokens.expiresIn * 1000).toString());
  }

  /**
   * Store backend authentication data (auth token and user)
   * This is the token and user data returned by the backend after OAuth validation
   */
  private storeBackendAuthData(authResponse: BackendAuthResponse): void {
    // Store backend auth token - this is what we'll use for API calls
    localStorage.setItem('auth_token', authResponse.authToken);
    localStorage.setItem(
      'auth_token_expiry',
      (Date.now() + authResponse.expiresIn * 1000).toString()
    );

    if (authResponse.refreshToken) {
      localStorage.setItem('auth_refresh_token', authResponse.refreshToken);
    }

    // Store user data for session persistence
    localStorage.setItem('auth_user', JSON.stringify(authResponse.user));
  }

  private getStoredToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private getStoredRefreshToken(): string | null {
    return localStorage.getItem('refreshToken');
  }

  private clearStoredToken(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');
  }

  /**
   * Clear all backend auth data
   */
  private clearBackendAuthData(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('auth_token_expiry');
    localStorage.removeItem('auth_user');
  }

  isTokenExpired(): boolean {
    // Check backend auth token expiry first (primary)
    const backendExpiry = localStorage.getItem('auth_token_expiry');
    if (backendExpiry && Date.now() < parseInt(backendExpiry)) {
      return false;
    }

    // Check OAuth token expiry
    const oauthExpiry = localStorage.getItem('oauth_token_expiry');
    if (oauthExpiry && Date.now() < parseInt(oauthExpiry)) {
      return false;
    }

    // Check legacy token expiry
    const expiry = localStorage.getItem('tokenExpiry');
    if (expiry && Date.now() < parseInt(expiry)) {
      return false;
    }

    return true;
  }

  /**
   * Check if a refresh token is available (any type)
   */
  hasRefreshToken(): boolean {
    return !!(
      localStorage.getItem('oauth_refresh_token') ||
      localStorage.getItem('auth_refresh_token') ||
      localStorage.getItem('refreshToken')
    );
  }

  getAuthToken(): string | null {
    // Return stored token regardless of expiry.
    // Let the API validate the token; if expired the interceptor
    // handles 401 → refresh → retry transparently.

    // Priority 1: Backend auth token (used after OAuth validation)
    const backendToken = localStorage.getItem('auth_token');
    if (backendToken) return backendToken;

    // Priority 2: OAuth token (during OAuth flow)
    const oauthToken = localStorage.getItem('oauth_access_token');
    if (oauthToken) return oauthToken;

    // Priority 3: Legacy token (traditional login)
    return this.getStoredToken();
  }

  /**
   * Token refresh coordination for the auth interceptor.
   * Prevents multiple concurrent refresh attempts when several requests get 401 simultaneously.
   */
  isRefreshInProgress(): boolean {
    return this._isRefreshing;
  }

  startRefresh(): void {
    this._isRefreshing = true;
    this._refreshTokenSubject.next(null);
  }

  completeRefresh(token: string | null): void {
    this._isRefreshing = false;
    // Always emit so queued requests unblock.
    // Empty string signals failure; real token string signals success.
    this._refreshTokenSubject.next(token ?? '');
  }

  /**
   * Observable for queued requests to wait on.
   * Emits the new token on success or an empty string on failure.
   * Skips the initial `null` (set by startRefresh) so subscribers
   * only receive the final result.
   */
  getRefreshToken$(): Observable<string> {
    return this._refreshTokenSubject.pipe(
      filter((t): t is string => t !== null),
      take(1)
    );
  }

  // DEVELOPMENT ONLY: Mock authentication for testing navigation
  private enableMockAuthentication(): void {
    if (!environment.production) {
      const mockUser: AuthUser = {
        id: 'mock-user-001',
        email: 'demo@walgreens.com',
        name: 'Demo User',
        roles: ['Admin', 'Manager'],
        permissions: ['read', 'write', 'delete', 'manage_assets', 'manage_locations'],
      };

      // Set mock user as authenticated
      this._user.set(mockUser);

      // Store mock token to simulate authentication
      this.storeMockToken();
    }
  }

  // Mock login for development
  mockLogin(): void {
    this.enableMockAuthentication();
  }

  /**
   * Store OAuth tokens for cross-port authentication
   */
  private storeOAuthTokens(tokenResponse: OAuth2TokenResponse): void {
    // Store tokens in localStorage for cross-port access
    localStorage.setItem('oauth_access_token', tokenResponse.access_token);
    if (tokenResponse.refresh_token) {
      localStorage.setItem('oauth_refresh_token', tokenResponse.refresh_token);
    } else {
      console.warn(
        '[AuthService] No refresh_token in response! Ensure "offline_access" is in the OAuth scope.'
      );
    }
    localStorage.setItem(
      'oauth_token_expiry',
      (Date.now() + (tokenResponse.expires_in || 3600) * 1000).toString()
    );

    // Also store as regular access token for compatibility
    localStorage.setItem('accessToken', tokenResponse.access_token);
    localStorage.setItem(
      'tokenExpiry',
      (Date.now() + (tokenResponse.expires_in || 3600) * 1000).toString()
    );
  }

  /**
   * Clear all OAuth data and backend auth data
   */
  clearOAuthData(): void {
    localStorage.removeItem('oauth_access_token');
    localStorage.removeItem('oauth_refresh_token');
    localStorage.removeItem('oauth_token_expiry');
    localStorage.removeItem('oauth_user');
    this.clearStoredToken();
    this.clearBackendAuthData();
  }
}
